// 1. React and core hooks
import { useEffect, useRef } from 'react';

// 2. External libraries
import { useClerk } from '@clerk/clerk-react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

// 3. Internal components
import { FullPageSpinner } from '~/components/common/FullPageSpinner';

// 4. Type imports
import type { AttributionData } from '~/types/onboarding';

// 5. Custom hooks and utilities (§TS.4.2 - alphabetized)
import { RETURN_URL_KEY } from '~/components/auth/RedirectToSignInWithReturn';
import { ATTRIBUTION_CALLBACK_TIMEOUT_MS } from '~/utils/attributionConstants';
import { extractAuthErrorCode, extractClerkErrorCode } from '~/utils/clerkErrorHandler';
import { useAuth } from '~/hooks/useAuth';
import { useAttributionStorage } from '~/hooks/useAttribution';
import { useUserMetadataMutation } from '~/hooks/useUserMetadataMutation';

// Routes that should bypass onboarding redirect after login
const ONBOARDING_BYPASS_ROUTES = ['/telegram/connect'];

const routeApi = getRouteApi('/sso-callback');

// Every page handleRedirectCallback can send a user to. clerk-js navigates to
// these itself: useClerk() returns clerk-react's IsomorphicClerk, which drops a
// custom navigate callback and swallows errors. Clerk falls back to its hosted
// Account Portal for each one not passed here (it ignores ClerkProvider's
// signInUrl).
// - signInUrl and signUpUrl return to this page with `failed` set, so this page
//   can read the failure off clerk.client and route it.
// - The remaining URLs are additional-verification steps (first/second factor,
//   password reset, sign-up continuation, email/phone verification). They go to
//   the auth-continue page, which explains that the step is not supported here.
// The `failed` value must stay a non-JSON word: TanStack Router JSON-parses
// search values, so `failed=1` would arrive as the number 1 and `failed=true` as
// a boolean. The schema turns any JSON-parseable value into undefined, and this
// page would then call handleRedirectCallback again, which is the loop the
// failed flag exists to prevent.
const FAILED_CALLBACK_URL = '/sso-callback?failed=clerk';
const AUTH_CONTINUE_URL = '/auth-continue';
const REDIRECT_CALLBACK_URLS = {
  signInUrl: FAILED_CALLBACK_URL,
  signUpUrl: FAILED_CALLBACK_URL,
  firstFactorUrl: AUTH_CONTINUE_URL,
  secondFactorUrl: AUTH_CONTINUE_URL,
  resetPasswordUrl: AUTH_CONTINUE_URL,
  continueSignUpUrl: AUTH_CONTINUE_URL,
  verifyEmailAddressUrl: AUTH_CONTINUE_URL,
  verifyPhoneNumberUrl: AUTH_CONTINUE_URL,
};

/**
 * Sends a failed SSO callback to the auth-error page when the classified
 * failure asks for a redirect, passing its scenario as the error type.
 * Otherwise sends the user back to sign-in, with the failure code as the
 * reason when one exists.
 */
async function navigateForCallbackFailure(
  navigate: ReturnType<typeof useNavigate>,
  result: ReturnType<typeof extractClerkErrorCode>
): Promise<void> {
  if (result.shouldRedirect) {
    await navigate({
      to: '/auth-error',
      search: { type: result.scenario },
      replace: true,
    });
    return;
  }
  await navigate({
    to: '/sign-in',
    search: result.code ? { reason: result.code } : {},
    replace: true,
  });
}

export function SsoCallbackPage() {
  const clerk = useClerk();
  const { handleRedirectCallback } = clerk;
  const { user, isLoaded } = useAuth();
  const { getAttribution, clearAttribution } = useAttributionStorage();
  const navigate = useNavigate();
  const { redirect_url: redirectUrl, failed } = routeApi.useSearch();
  const metadataMutation = useUserMetadataMutation();

  // Get the redirect URL from query params OR sessionStorage (set by RedirectToSignInWithReturn)
  // sessionStorage is more reliable as query params can be lost during OAuth flows
  // IMPORTANT: Use ref to capture redirect URL on MOUNT only - ensures value is stable across all renders (§TS.5.2)
  const redirectUrlRef = useRef<string | null>(
    redirectUrl || sessionStorage.getItem(RETURN_URL_KEY)
  );

  // Prevent multiple executions of the attribution write flow
  const isProcessingRef = useRef(false);

  useEffect(() => {
    async function processCallback(): Promise<void> {
      // Skip handleRedirectCallback if user is already loaded - just let the second useEffect handle navigation
      if (isLoaded && user) {
        return;
      }

      // clerk-js sent the user back here after a failed callback. Calling
      // handleRedirectCallback again would loop, so read the reason instead.
      if (failed) {
        if (!isLoaded) {
          return;
        }
        const code =
          clerk.client?.signUp.verifications.externalAccount.error?.code ??
          clerk.client?.signIn.firstFactorVerification.error?.code;
        await navigateForCallbackFailure(navigate, extractClerkErrorCode({ errors: [{ code }] }));
        return;
      }

      try {
        // Handle the OAuth redirect callback
        // Using signInFallbackRedirectUrl per Clerk docs - only used if no redirect_url in OAuth state
        await handleRedirectCallback({
          signInFallbackRedirectUrl: '/sso-callback',
          ...REDIRECT_CALLBACK_URLS,
        });
      } catch (error) {
        // Covers the not-yet-loaded path, where the callback throws
        await navigateForCallbackFailure(navigate, extractClerkErrorCode(error));
      }
    }

    void processCallback();
  }, [clerk, handleRedirectCallback, navigate, isLoaded, user, failed]);

  useEffect(() => {
    async function writeAttributionAndNavigate(): Promise<void> {
      if (!isLoaded || !user) {
        return;
      }

      // Prevent concurrent executions
      if (isProcessingRef.current) {
        return;
      }
      isProcessingRef.current = true;

      // Check redirect URL FIRST before any async operations
      const redirectUrl = redirectUrlRef.current;

      // Check for bypass routes FIRST - skip attribution write for faster redirect
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl, window.location.origin);
          const pathname = url.pathname;
          const shouldBypassOnboarding = ONBOARDING_BYPASS_ROUTES.some((route) =>
            pathname.startsWith(route)
          );

          if (shouldBypassOnboarding) {
            // Clear the stored return URL
            sessionStorage.removeItem(RETURN_URL_KEY);
            const targetUrl = url.pathname + url.search;
            // Use window.location for hard navigation - the router navigate() gets overridden by Clerk
            window.location.href = targetUrl;
            return;
          }
        } catch {
          // Invalid URL, fall through to default behavior
        }
      }

      // If not bypassing, proceed with attribution write
      // Read attribution from sessionStorage
      const attribution = getAttribution();

      // Write attribution to Clerk publicMetadata via backend API with timeout and retry
      let writeSuccess = false;
      const maxAttempts = 2;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const updatePromise = metadataMutation.mutateAsync({
            attribution:
              attribution ||
              ({
                referrer: null,
                utmSource: null,
                utmMedium: null,
                utmCampaign: null,
                utmContent: null,
                utmTerm: null,
              } satisfies AttributionData),
          });

          // Wait for update with timeout (§BASIC.6 - avoid unnecessary abstraction)
          await Promise.race([
            updatePromise,
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('Attribution write timeout')),
                ATTRIBUTION_CALLBACK_TIMEOUT_MS
              )
            ),
          ]);

          writeSuccess = true;
          break;
        } catch (error) {
          // Check if this is a restriction error that should redirect to auth error page
          const errorResult = extractAuthErrorCode(error);
          if (errorResult.shouldRedirect) {
            void navigate({
              to: '/auth-error',
              search: { type: errorResult.scenario },
              replace: true,
            });
            return; // Exit the function, don't continue with navigation
          }
        }
      }

      // Clear attribution from sessionStorage after write attempt
      if (writeSuccess) {
        clearAttribution();
      }

      // Check onboarding status and navigate
      const onboardingComplete = user.publicMetadata?.onboardingComplete === true;

      // Clear the stored return URL before navigating
      sessionStorage.removeItem(RETURN_URL_KEY);

      if (onboardingComplete) {
        void navigate({ to: '/', replace: true });
      } else {
        void navigate({ to: '/onboarding', replace: true });
      }
    }

    void writeAttributionAndNavigate();
    // Note: metadataMutation excluded from deps to prevent infinite loop - ref guards against multiple calls
    // Note: redirectUrlRef excluded from deps - it's a ref that captures value on mount (§TS.5.2)
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isLoaded, user, getAttribution, clearAttribution, navigate]);

  return <FullPageSpinner message="Completing sign-in..." />;
}
